import { useCallback, useEffect, useRef } from 'react'

import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'

import { KeyCodes } from '@/consts/keys.ts'

import { useMessageEditorContext } from './message-editor-provider'

/**
 * A custom hook for managing the editor state and functionality.
 *
 * This hook provides methods to:
 * - Register and unregister an Enter key handler plugin
 * - Reset the editor content
 * - Focus and blur the editor
 * - Insert content into the editor
 * - Set the editor content
 *
 * It also handles cleanup of the Enter key handler plugin on unmount.
 *
 * Note: This hook should only be used inside EditorProvider to work correctly.
 *
 * @returns An object containing the editor instance and various utility functions.
 */
export const useEditor = () => {
  const { editor, text, message } = useMessageEditorContext()
  const pluginKey = useRef<PluginKey | null>(null)

  const getEventHandlerPlugin = useCallback(
    ({
      key,
      order,
      enterHandler,
      keydownHandler,
      keyupHandler,
      inputHandler,
    }: {
      key: PluginKey
      order: number
      enterHandler?: (event: KeyboardEvent) => void
      keydownHandler?: (event: KeyboardEvent) => void
      keyupHandler?: (event: KeyboardEvent) => void
      inputHandler?: (event: Event) => void
    }) => {
      return new Plugin({
        key,
        order,
        props: {
          handleDOMEvents: {
            keydown: (_: EditorView, event: KeyboardEvent) => {
              if (event.key === KeyCodes.ENTER && !event.shiftKey) {
                enterHandler?.(event)

                return false
              }
              keydownHandler?.(event)

              return false
            },
            keyup: (_: EditorView, event: KeyboardEvent) => {
              keyupHandler?.(event)

              return false
            },
            input: (_, event) => {
              inputHandler?.(event)

              return false
            },
          },
        },
      })
    },
    [],
  )

  const unregisterEventHandlerPlugin = useCallback(() => {
    if (pluginKey.current) {
      editor?.unregisterPlugin(pluginKey.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const registerEventHandlerPlugin = useCallback(
    ({
      key,
      order,
      enterHandler,
      keydownHandler,
      keyupHandler,
      inputHandler,
    }: {
      key: PluginKey
      order: number
      enterHandler?: (event: KeyboardEvent) => void
      keydownHandler?: (event: KeyboardEvent) => void
      keyupHandler?: (event: KeyboardEvent) => void
      inputHandler?: (event: Event) => void
    }) => {
      unregisterEventHandlerPlugin()

      pluginKey.current = key
      editor?.registerPlugin(
        getEventHandlerPlugin({
          key,
          order,
          enterHandler,
          keydownHandler,
          keyupHandler,
          inputHandler,
        }),
        // To make sure handlers will be executed in the correct order despite the registration order
        (newPlugin, plugins) =>
          [newPlugin, ...plugins].sort((a, b) => a.spec.order - b.spec.order),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getEventHandlerPlugin, unregisterEventHandlerPlugin],
  )

  useEffect(() => {
    const key = pluginKey.current

    return () => {
      unregisterEventHandlerPlugin()
      if (key) {
        editor?.unregisterPlugin(key)
      }
    }
  }, [editor, registerEventHandlerPlugin, unregisterEventHandlerPlugin])

  const handleReset = useCallback(() => {
    editor?.commands.clearContent(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFocus = useCallback(() => {
    editor?.commands.focus('end')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBlur = useCallback(() => {
    editor?.commands.blur()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetContent = useCallback(
    (content: string, reset = false) => {
      if (reset) {
        editor
          ?.chain()
          .clearContent()
          .setContent(formatContent(content), true)
          .run()
      } else {
        editor?.chain().setContent(formatContent(content), true).run()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleInsertContent = useCallback(
    (content: string) => {
      editor?.chain().focus().insertContent(formatContent(content)).run()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleSetHtml = useCallback(
    (html: string) => {
      editor?.commands.setContent(html)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const formatContent = useCallback(
    (content: string) => content.replaceAll('\n', '<br>'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return {
    editor,
    text,
    message,
    handleReset,
    handleFocus,
    handleBlur,
    handleSetContent,
    handleSetHtml,
    handleInsertContent,
    registerEnterHandlerPlugin: registerEventHandlerPlugin,
    unregisterEnterHandlerPlugin: unregisterEventHandlerPlugin,
  }
}
